/* eslint-disable prettier/prettier */
import React, { createContext, useContext, useState } from 'react';
import { ClientInfo, LegalInformation, AgentInstructions } from './types';

interface AuditContextType {
  // Selected client
  selectedClient: ClientInfo | null;
  setSelectedClient: (client: ClientInfo | null) => void;
  
  // Conversation state
  conversationStage: 'ready' | 'in_conversation' | 'finished';
  setConversationStage: (stage: 'ready' | 'in_conversation' | 'finished') => void;
  
  // System messages
  systemMessages: {id: string; type: string; message: string; details?: string; timestamp: Date}[];
  addSystemMessage: (type: string, message: string, details?: string) => void;
  
  // Legal information reported by agent
  legalInformation: LegalInformation[];
  addLegalInformation: (info: Omit<LegalInformation, 'id' | 'timestamp'>) => void;
  clearLegalInformation: () => void;
  
  // Agent instructions
  agentInstructions: AgentInstructions;
  updateAgentInstructions: (instructions: string) => void;
}

const AuditContext = createContext<AuditContextType | undefined>(undefined);

export const AuditProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  // Basic state
  const [selectedClient, setSelectedClient] = useState<ClientInfo | null>(null);
  
  // Conversation state
  const [conversationStage, setConversationStage] = useState<
    'ready' | 'in_conversation' | 'finished'
  >('ready');
  
  // System messages
  const [systemMessages, setSystemMessages] = useState<
    {id: string; type: string; message: string; details?: string; timestamp: Date}[]
  >([]);
  
  // Legal information
  const [legalInformation, setLegalInformation] = useState<LegalInformation[]>([]);
  
  // Agent instructions
  const [agentInstructions, setAgentInstructions] = useState<AgentInstructions>({
    instructions: '',
    lastUpdated: new Date()
  });
  
  // Function to add system messages
  const addSystemMessage = (type: string, message: string, details?: string) => {
    setSystemMessages(prev => [
      {
        id: Math.random().toString(36).substring(2, 11),
        type,
        message,
        details,
        timestamp: new Date()
      },
      ...prev
    ]);
  };
  
  // Function to add legal information
  const addLegalInformation = (info: Omit<LegalInformation, 'id' | 'timestamp'>) => {
    setLegalInformation(prev => [
      {
        ...info,
        id: Math.random().toString(36).substring(2, 11),
        timestamp: new Date()
      },
      ...prev
    ]);
  };
  
  // Function to clear legal information
  const clearLegalInformation = () => {
    setLegalInformation([]);
  };
  
  // Function to update agent instructions
  const updateAgentInstructions = (instructions: string) => {
    setAgentInstructions({
      instructions,
      lastUpdated: new Date()
    });
  };

  const value = {
    selectedClient,
    setSelectedClient,
    conversationStage,
    setConversationStage,
    systemMessages,
    addSystemMessage,
    legalInformation,
    addLegalInformation,
    clearLegalInformation,
    agentInstructions,
    updateAgentInstructions
  };

  return (
    <AuditContext.Provider value={value}>
      {children}
    </AuditContext.Provider>
  );
};

export const useAudit = () => {
  const context = useContext(AuditContext);
  if (context === undefined) {
    throw new Error('useAudit must be used within an AuditProvider');
  }
  return context;
};
